// F-080 — Budget prévisionnel, scénarios et écarts réalisé / prévu. Les hypothèses restent séparées du réel.
import { useState } from "react";
import { del, post, put } from "@core/api";
import { useGet } from "@core/hooks";
import { percent } from "@core/format";
import { useOrg } from "@core/org";
import { Badge, Button, Card, Empty, ErrorBox, Input, Loading, Modal, MoneyInput, Notice, PageHeader, Select, Stat, Table, Textarea, fieldError } from "@core/ui";
import { Amount, CompareBars, PropertySelect, YearSelect, bpToInput, currentYear, percentToBp, useAction, useCatalog, useRefs } from "./shared";

type Line = { id: string; year: number; kind: "recette" | "depense"; category: string; label: string; amount_cents: number; property_id: string | null; property_label: string | null; notes: string };
type Scenario = { id: string; year: number; name: string; vacancy_weeks: number; rent_change_bp: number; tax_increase_bp: number; other_expenses_change_bp: number; extra_works_cents: number; notes: string };
type Variance = { kind: "recette" | "depense"; category: string; label: string; planned_cents: number; realized_cents: number; diff_cents: number; diff_bp: number | null; status: string };
type Projection = {
  base_result_cents: number;
  rent_base_cents: number;
  rent_projected_cents: number;
  tax_base_cents: number;
  tax_projected_cents: number;
  other_expenses_base_cents: number;
  other_expenses_projected_cents: number;
  extra_works_cents: number;
  income_cents: number;
  expenses_cents: number;
  result_cents: number;
  result_change_cents: number;
};
type Overview = {
  lines: Line[];
  scenarios: Scenario[];
  variances: Variance[];
  planned: { income_cents: number; expenses_cents: number; result_cents: number };
  realized: { income_cents: number; expenses_cents: number; result_cents: number; to: string };
  projections: { scenario_id: string; name: string; projection: Projection }[];
  notes: string[];
};

const VARIANCE: Record<string, [string, "neutre" | "succes" | "attention"]> = {
  conforme: ["Dans le prévu", "succes"],
  ecart_important: ["Écart à examiner", "attention"],
  non_prevu: ["Non prévu", "neutre"],
  non_realise: ["Pas encore réalisé", "neutre"],
};

export function BudgetPage() {
  const org = useOrg();
  const [year, setYear] = useState(currentYear());
  const q = useGet<Overview>(org.api(`/finance/budget?year=${year}`));
  const [line, setLine] = useState<Line | "new" | null>(null);
  const [scenario, setScenario] = useState<Scenario | "new" | null>(null);
  const a = useAction();
  const canEdit = org.can("finance");
  const d = q.data;
  return (
    <div className="page">
      <PageHeader
        eyebrow="Finances"
        title="Budget prévisionnel"
        intro="Prévoyez recettes et dépenses de l’année, comparez-les au réalisé et testez des scénarios de vacance, de travaux ou de hausse de taxe. Ce sont des hypothèses : aucun résultat n’est garanti."
        actions={
          canEdit ? (
            <>
              <Button variant="secondaire" onClick={() => setScenario("new")}>
                Nouveau scénario
              </Button>
              <Button onClick={() => setLine("new")}>Ajouter une ligne</Button>
            </>
          ) : null
        }
      />
      <div style={{ maxWidth: 200 }}>
        <YearSelect value={year} onChange={setYear} />
      </div>
      {q.isLoading ? <Loading /> : null}
      <ErrorBox error={q.error ?? a.error} />
      {d ? (
        <>
          <div className="stats">
            <Stat label="Recettes prévues" value={<Amount cents={d.planned.income_cents} />} hint={<>Réalisé : <Amount cents={d.realized.income_cents} /></>} />
            <Stat label="Dépenses prévues" value={<Amount cents={d.planned.expenses_cents} />} hint={<>Réalisé : <Amount cents={d.realized.expenses_cents} /></>} />
            <Stat label="Solde prévu" value={<Amount cents={d.planned.result_cents} />} hint={<>Réalisé : <Amount cents={d.realized.result_cents} /></>} />
          </div>
          {d.lines.length === 0 ? (
            <Empty title="Aucune ligne de budget pour cette année." action={canEdit ? <Button variant="secondaire" busy={a.busy} onClick={() => a.run(() => post(org.api("/finance/budget/propose"), { year }), { success: "Lignes proposées d’après l’année précédente.", refresh: [org.api("/finance/budget")] })}>Proposer d’après le réalisé {year - 1}</Button> : null}>
              Vous pouvez partir du réalisé de l’année précédente, puis ajuster chaque ligne.
            </Empty>
          ) : (
            <Card title="Lignes prévues">
              <Table caption="Lignes du budget">
                <thead>
                  <tr>
                    <th>Poste</th>
                    <th>Logement</th>
                    <th className="right">Montant annuel</th>
                    {canEdit ? <th><span className="sr-only">Actions</span></th> : null}
                  </tr>
                </thead>
                <tbody>
                  {d.lines.map((l) => (
                    <tr key={l.id}>
                      <td>
                        {l.label || d.variances.find((v) => v.category === l.category)?.label || l.category}
                        <span className="finances-sous">{l.kind === "recette" ? "Recette" : "Dépense"}{l.notes ? ` · ${l.notes}` : ""}</span>
                      </td>
                      <td>{l.property_label ?? "Tout le patrimoine"}</td>
                      <td className="right"><Amount cents={l.amount_cents} kind={l.kind} /></td>
                      {canEdit ? (
                        <td>
                          <div className="finances-actions">
                            <Button variant="discret" small onClick={() => setLine(l)}>Modifier</Button>
                            <Button variant="discret" small onClick={() => a.run(() => del(org.api(`/finance/budget/lines/${l.id}`)), { success: "Ligne supprimée.", refresh: [org.api("/finance/budget")] })}>Supprimer</Button>
                          </div>
                        </td>
                      ) : null}
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card>
          )}
          {d.variances.length ? (
            <Card title="Réalisé et prévu">
              <CompareBars caption={`Montants prévus et réalisés par poste en ${year}.`} rows={d.variances.map((v) => ({ label: v.label, planned: v.planned_cents, realized: v.realized_cents }))} />
              <Table caption="Écarts par poste">
                <thead>
                  <tr>
                    <th>Poste</th>
                    <th className="right">Prévu</th>
                    <th className="right">Réalisé</th>
                    <th className="right">Écart</th>
                    <th>Constat</th>
                  </tr>
                </thead>
                <tbody>
                  {d.variances.map((v) => {
                    const [label, tone] = VARIANCE[v.status] ?? [v.status, "neutre"];
                    return (
                      <tr key={`${v.kind}-${v.category}`}>
                        <td>
                          {v.label}
                          <span className="finances-sous">{v.kind === "recette" ? "Recette" : "Dépense"}</span>
                        </td>
                        <td className="right"><Amount cents={v.planned_cents} /></td>
                        <td className="right"><Amount cents={v.realized_cents} /></td>
                        <td className="right">
                          <Amount cents={v.diff_cents} />
                          {v.diff_bp !== null ? <span className="finances-sous">{percent(v.diff_bp)}</span> : null}
                        </td>
                        <td><Badge tone={tone}>{label}</Badge></td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </Card>
          ) : null}
          <Card title="Scénarios">
            {d.projections.length === 0 ? <p className="muted">Aucun scénario. Un scénario applique vos hypothèses aux lignes prévues, sans toucher au réel.</p> : null}
            <div className="grid-2">
              {d.projections.map((p) => {
                const s = d.scenarios.find((x) => x.id === p.scenario_id)!;
                const pr = p.projection;
                return (
                  <div key={p.scenario_id} className="finances-element">
                    <div className="finances-element-tete">
                      <h3>{p.name}</h3>
                      {canEdit ? (
                        <div className="finances-actions">
                          <Button variant="discret" small onClick={() => setScenario(s)}>Modifier</Button>
                          <Button variant="discret" small onClick={() => a.run(() => del(org.api(`/finance/budget/scenarios/${s.id}`)), { success: "Scénario supprimé.", refresh: [org.api("/finance/budget")] })}>Supprimer</Button>
                        </div>
                      ) : null}
                    </div>
                    <p className="small muted">
                      Vacance {s.vacancy_weeks} semaine{s.vacancy_weeks > 1 ? "s" : ""} · loyer {percent(s.rent_change_bp)} · taxe foncière {percent(s.tax_increase_bp)} · autres dépenses {percent(s.other_expenses_change_bp)} · travaux <Amount cents={s.extra_works_cents} />
                    </p>
                    <Table caption={`Projection ${p.name}`}>
                      <tbody>
                        <tr><td>Loyers et provisions</td><td className="right"><Amount cents={pr.rent_base_cents} /> → <Amount cents={pr.rent_projected_cents} /></td></tr>
                        <tr><td>Taxe foncière</td><td className="right"><Amount cents={pr.tax_base_cents} /> → <Amount cents={pr.tax_projected_cents} /></td></tr>
                        <tr><td>Autres dépenses</td><td className="right"><Amount cents={pr.other_expenses_base_cents} /> → <Amount cents={pr.other_expenses_projected_cents} /></td></tr>
                        <tr><td>Travaux supplémentaires</td><td className="right"><Amount cents={pr.extra_works_cents} /></td></tr>
                        <tr><td><strong>Solde simulé</strong></td><td className="right"><strong><Amount cents={pr.result_cents} /></strong><span className="finances-sous">écart avec le budget : <Amount cents={pr.result_change_cents} /></span></td></tr>
                      </tbody>
                    </Table>
                  </div>
                );
              })}
            </div>
          </Card>
          <Notice title="Hypothèses et réalisé">
            <ul className="finances-formules">
              {d.notes.map((n) => (
                <li key={n}>{n}</li>
              ))}
            </ul>
          </Notice>
        </>
      ) : null}
      {line ? <LineModal year={year} line={line === "new" ? null : line} onClose={() => setLine(null)} /> : null}
      {scenario ? <ScenarioModal year={year} scenario={scenario === "new" ? null : scenario} onClose={() => setScenario(null)} /> : null}
    </div>
  );
}

function LineModal({ year, line, onClose }: { year: number; line: Line | null; onClose: () => void }) {
  const org = useOrg();
  const refs = useRefs();
  const catalog = useCatalog();
  const [f, setF] = useState({ kind: line?.kind ?? "depense", category: line?.category ?? "", label: line?.label ?? "", amount_cents: line?.amount_cents ?? null as number | null, property_id: line?.property_id ?? "", notes: line?.notes ?? "" });
  const a = useAction();
  const cats = (catalog.data?.categories ?? []).filter((c) => c.kind === f.kind && c.code !== "encaissement_non_ventile");
  return (
    <Modal
      open
      onClose={onClose}
      title={line ? "Modifier la ligne" : "Nouvelle ligne de budget"}
      footer={
        <>
          <Button variant="secondaire" onClick={onClose}>Fermer</Button>
          <Button
            busy={a.busy}
            onClick={async () => {
              const body = { ...f, year, amount_cents: f.amount_cents ?? -1, property_id: f.property_id || null };
              const r = await a.run(() => (line ? put(org.api(`/finance/budget/lines/${line.id}`), body) : post(org.api("/finance/budget/lines"), body)), { success: "Ligne enregistrée.", refresh: [org.api("/finance/budget")] });
              if (r) onClose();
            }}
          >
            Enregistrer
          </Button>
        </>
      }
    >
      <div className="stack">
        <ErrorBox error={a.error && !(fieldError(a.error, "category") || fieldError(a.error, "amount_cents")) ? a.error : null} />
        <div className="form-row">
          <Select label="Type" value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value as "recette" | "depense", category: "" })} options={[{ value: "depense", label: "Dépense" }, { value: "recette", label: "Recette" }]} />
          <Select label="Poste" required value={f.category} error={fieldError(a.error, "category")} onChange={(e) => setF({ ...f, category: e.target.value })} options={[{ value: "", label: "Choisir…" }, ...cats.map((c) => ({ value: c.code, label: c.label }))]} />
        </div>
        <Input label="Libellé (facultatif)" value={f.label} onChange={(e) => setF({ ...f, label: e.target.value })} />
        <div className="form-row">
          <MoneyInput label="Montant annuel prévu" required cents={f.amount_cents} error={fieldError(a.error, "amount_cents")} onChange={(c) => setF({ ...f, amount_cents: c })} />
          <PropertySelect refs={refs.data} value={f.property_id} onChange={(v) => setF({ ...f, property_id: v })} empty="Tout le patrimoine" />
        </div>
        <Textarea label="Hypothèse retenue" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} />
      </div>
    </Modal>
  );
}

function ScenarioModal({ year, scenario, onClose }: { year: number; scenario: Scenario | null; onClose: () => void }) {
  const org = useOrg();
  const [f, setF] = useState({
    name: scenario?.name ?? "",
    vacancy_weeks: String(scenario?.vacancy_weeks ?? 0),
    rent: scenario ? bpToInput(scenario.rent_change_bp) : "0",
    tax: scenario ? bpToInput(scenario.tax_increase_bp) : "0",
    other: scenario ? bpToInput(scenario.other_expenses_change_bp) : "0",
    extra_works_cents: scenario?.extra_works_cents ?? (null as number | null),
    notes: scenario?.notes ?? "",
  });
  const a = useAction();
  const bad = [percentToBp(f.rent), percentToBp(f.tax), percentToBp(f.other)].some((x) => x === null);
  return (
    <Modal
      open
      onClose={onClose}
      title={scenario ? "Modifier le scénario" : "Nouveau scénario"}
      footer={
        <>
          <Button variant="secondaire" onClick={onClose}>Fermer</Button>
          <Button
            busy={a.busy}
            disabled={bad}
            onClick={async () => {
              const body = {
                year,
                name: f.name,
                vacancy_weeks: parseInt(f.vacancy_weeks || "0", 10),
                rent_change_bp: percentToBp(f.rent),
                tax_increase_bp: percentToBp(f.tax),
                other_expenses_change_bp: percentToBp(f.other),
                extra_works_cents: f.extra_works_cents ?? 0,
                notes: f.notes,
              };
              const r = await a.run(() => (scenario ? put(org.api(`/finance/budget/scenarios/${scenario.id}`), body) : post(org.api("/finance/budget/scenarios"), body)), { success: "Scénario enregistré.", refresh: [org.api("/finance/budget")] });
              if (r) onClose();
            }}
          >
            Enregistrer
          </Button>
        </>
      }
    >
      <div className="stack">
        <p className="muted">Un scénario est une hypothèse. Il est recalculé à partir des lignes prévues à chaque affichage.</p>
        <ErrorBox error={a.error && !fieldError(a.error, "name") ? a.error : null} />
        <Input label="Nom du scénario" required value={f.name} error={fieldError(a.error, "name")} onChange={(e) => setF({ ...f, name: e.target.value })} />
        <div className="form-row">
          <Input label="Vacance (semaines sur 52)" inputMode="numeric" value={f.vacancy_weeks} error={fieldError(a.error, "vacancy_weeks")} onChange={(e) => setF({ ...f, vacancy_weeks: e.target.value.replace(/\D/g, "") })} />
          <Input label="Variation des loyers (%)" inputMode="decimal" value={f.rent} error={percentToBp(f.rent) === null ? "Indiquez un pourcentage, par exemple 2 ou -1,5." : undefined} onChange={(e) => setF({ ...f, rent: e.target.value })} />
        </div>
        <div className="form-row">
          <Input label="Hausse de la taxe foncière (%)" inputMode="decimal" value={f.tax} error={percentToBp(f.tax) === null ? "Indiquez un pourcentage." : undefined} onChange={(e) => setF({ ...f, tax: e.target.value })} />
          <Input label="Variation des autres dépenses (%)" inputMode="decimal" value={f.other} error={percentToBp(f.other) === null ? "Indiquez un pourcentage." : undefined} onChange={(e) => setF({ ...f, other: e.target.value })} />
        </div>
        <MoneyInput label="Travaux supplémentaires" cents={f.extra_works_cents} onChange={(c) => setF({ ...f, extra_works_cents: c })} />
        <Textarea label="Notes" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} />
      </div>
    </Modal>
  );
}
